<template>
  <div class="sourcery__container">
    <div class="d-flex flex-wrap gap-6">
      <v-col
        v-for="plan in pricingData"
        :key="`box-${plan.type}`"
        class="d-flex flex-column justify-start pa-4 lightGrey rounded-lg">
        <span class="text-uppercase">
          {{ plan.name }}
        </span>
        <div class="mt-4">
          <span class="fs-66">${{ plan.price }}</span>
          <span>/mo</span>
        </div>
        <span>per workspace</span>
        <v-btn
          :disabled="isActivePlan(plan.type)"
          :loading="isFetchingWSSubs"
          class="white--text mt-4"
          color="black"
          data-test="upgrade_workspace_category_button"
          elevation="0"
          @click="openPaymentModal(plan)">
          {{ planButtonText(plan.type) }}
        </v-btn>
      </v-col>
    </div>

    <v-simple-table class="mt-10">
      <template v-slot:default>
        <thead>
          <tr>
            <th class="text-left">
              Features
            </th>
            <th
              v-for="plan in pricingData"
              :key="plan.type"
              class="text-center">
              {{ plan.name }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="feature in features"
            :key="feature.key">
            <td>
              {{ feature.label }}
            </td>
            <td
              v-for="plan in pricingData"
              :key="`${plan.type}-${feature.key}`"
              class="text-center">
              <span class="text-capitalize">
                {{ plan[feature.key] }}
              </span>
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>

    <div class="d-flex flex-column align-center pa-4 mt-10 lightGrey rounded-lg">
      <span class="text-uppercase">
        Add Unlimited Users and Guests
      </span>
      <div class="mt-4">
        <span class="fs-66">$50</span>
        <span>/mo</span>
      </div>
      <v-btn
        :disabled="hasUnlimitedUsers"
        :loading="isFetchingWSSubs"
        class="white--text mt-4"
        color="black"
        data-test="upgrade_workspace_category_button"
        elevation="0"
        width="330"
        max-width="100%"
        @click="openPaymentModal({ name: 'Unlimited Users', type: 'unlimited_users' })">
        {{ hasUnlimitedUsers ? "Activated" : "Purchase" }}
      </v-btn>
    </div>

    <app-dialog
      v-model="showPaymentModal"
      width="600"
      content-class="v-dialog__form">
      <v-card>
        <v-card-title class="align-center">
          <span>{{ selectedPlanName.charAt(0).toUpperCase() + selectedPlanName.slice(1) + ' Plan' }}</span>
          <span
            class="sourcery__icon-wrapper black"
            @click="closePaymentModal">
            <v-icon
              color="white"
              size="15">
              mdi-close
            </v-icon>
          </span>
        </v-card-title>
        <v-card-text class="card-text">
          <v-form>
            <PaymentsCard
              v-if="paymentType === 'PaymentsCard'"
              :user-payment-state="userPaymentState"
              :is-ws-payment="true"
              :sorted-prices="sortedPricesForSelectedPlan"
              :user-subscriptions="userSubscriptions"
              @paymentModel="closePaymentModal"
              @stripeCreateWorkspaceSubscribe="stripeCreateWorkspaceSubscribe"
              v-on="$listeners" />

            <PaymentUnlimitedUsers
              v-if="paymentType === 'PaymentUnlimitedUsers'"
              :user-payment-state="userPaymentState"
              @paymentModel="closePaymentModal"
              @stripeSubUnlimitedUsers="stripeSubUnlimitedUsers"
              v-on="$listeners" />
          </v-form>
        </v-card-text>
      </v-card>
    </app-dialog>
  </div>
</template>

<script>
import pricingPlansData from '@/dataFront/pricing-plans.json';
import {
  mapActions,
  mapGetters,
  mapMutations,
  mapState,
} from 'vuex';

import {
  TYPE_WORKSPACE_FREE,
  ORDERED_WORKSPACE_TYPES,
  TYPE_WORKSPACE_EXPIRED,
  USER_PAYMENT_STATES,
} from '@/constants';
import Currency from '@/constants/currency';

import PaymentsCard from '@/components/PaymentV2/PaymentsCard';
import PaymentUnlimitedUsers from '@/components/PaymentV2/PaymentUnlimitedUsers';

import currencyController from '@/mixins/currencyController';

import PaymentsApi from '@/services/graphql/payments';
import WorkspacePaymentApi from '@/services/graphql/workspacePayment';

export default {
  name: 'PaymentV2',
  components: {
    PaymentsCard,
    PaymentUnlimitedUsers,
  },
  mixins: [currencyController],
  props: {
    isWsPayment: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      deactivateUnlimitedUsers: false,
      features: pricingPlansData.planFeatures,
      isFetchingPlans: true,
      // possible values: PaymentsCard, PaymentUnlimitedUsers
      paymentType: '',
      plansFromBack: pricingPlansData.plansFromBack,
      priceList: {
      },
      pricingData: pricingPlansData.pricingPlans,
      selectedPlan: '',
      selectedPlanName: '-',
      showPaymentModal: false,
      userSubscriptions: [],

      isFetchingWSSubs: false,
      latestWsSub: {
      },
      wsSubReqId: 0,
    };
  },
  computed: {
    ...mapState('Workspace', ['activeWorkspaceId']),
    ...mapGetters('Payment', ['getPreSelectedRole']),
    ...mapGetters({
      getActiveWorkspace: 'Workspace/getActiveWorkspace',
      getActiveWorkspaceType: 'Workspace/getActiveWorkspaceType',
      usePlatformPricingUpdates: 'FeatureFlags/usePlatformPricingUpdates',
    }),
    userPaymentState() {
      if (this.eligibleForTrial) {
        return USER_PAYMENT_STATES.eligibleForTrial;
      }
      if (this.isTrialing) {
        return USER_PAYMENT_STATES.trialing;
      }
      if (this.isExpiredPlan) {
        return this.latestWsSub.status === 'trialing'
          ? USER_PAYMENT_STATES.trialEnded
          : USER_PAYMENT_STATES.subEnded;
      }
      return USER_PAYMENT_STATES.subActive;
    },
    eligibleForTrial() {
      return this.getActiveWorkspaceType === TYPE_WORKSPACE_FREE;
    },
    isTrialing() {
      if (this.latestWsSub) {
        if (this.latestWsSub.status === 'trialing') {
          const subEnd = new Date(this.latestWsSub.subEnd);
          const now = new Date();
          return now < subEnd;
        }
      }
      return false;
    },
    isExpiredPlan() {
      return this.getActiveWorkspaceType === TYPE_WORKSPACE_EXPIRED;
    },
    getSelectedPlan() {
      return pricingPlansData;
    },
    hasUnlimitedUsers() {
      return this.deactivateUnlimitedUsers || this.getActiveWorkspace?.unlimitedUsers;
    },
    sortedPrices() {
      let sortedPrices = [];
      for (const key in this.priceList) {
        sortedPrices = sortedPrices.concat(this.priceList[key]);
      }
      sortedPrices.sort((a, b) => {
        return parseInt(b.unit_amount) - parseInt(a.unit_amount);
      });
      return sortedPrices;
    },
    sortedPricesForSelectedPlan() {
      const type = this.selectedPlan;
      if (!type) return [];
      return this.sortedPrices.filter((price) => {
        const priceType = price.metadata?.type?.replace(/[-_]/g, '').toLowerCase();
        const selectedType = type.replace(/[-_]/g, '').toLowerCase();
        return priceType === selectedType;
      });
    },
  },
  activeWorkspaceId() {
    this.fetchWSSubData();
  },
  getActiveWorkspaceType() {
    this.fetchWSSubData();
  },
  async mounted() {
    this.spinner(true);

    this.fetchWSSubData();

    await Promise.allSettled([
      this.initUserSubscriptions(),
      this.initCurrencyController(),
    ]);
    await this.getListPrice();

    this.spinner(false);
  },
  methods: {
    ...mapActions(['handleError']),
    ...mapActions('Payment', ['paymentIsProcessingNotification', 'trialActivatingNotification']),
    ...mapActions('Workspace', ['updateActiveWorkspaceData']),
    ...mapMutations(['spinner', 'clearParamsFromCustomView']),
    async initUserSubscriptions() {
      const { data } = await PaymentsApi.getUserSubscriptions();
      const { getUserSubsriptions: subs } = data ?? {
      };
      this.userSubscriptions = subs ?? [];
    },
    async fetchWSSubData() {
      this.isFetchingWSSubs = true;
      const id = ++this.wsSubReqId;

      try {
        const { data } = await WorkspacePaymentApi.getWorkspaceSubscription({
          workspaceId: this.activeWorkspaceId,
        });
        if (id !== this.wsSubReqId) return;

        const subs = data?.response ?? [];
        if (!subs.length || subs.length < 1) return;

        subs.sort((a, b) => new Date(b.subEnd) - new Date(a.subEnd));
        this.latestWsSub = subs[0];

      } finally {
        this.isFetchingWSSubs = false;
      }
    },
    /**
     * Stripe Callbacks below.
     */
    async getListPrice() {
      this.isFetchingPlans = true;
      try {
        const { currency = '' } = this.currencyController || {
        };
        await Promise.all(
          this.plansFromBack.map(async plan => {
            const prices = await PaymentsApi.getListPrice({
              filter: plan,
              currency,
            });
            this.$set(this.priceList, plan, prices);
          })
        );
      } catch (err) {
        console.error(err);
      } finally {
        this.isFetchingPlans = false;
      }
    },
    async stripeCreateWorkspaceSubscribe(
      selectedPrice,
      paymentMethodId,
      currency = Currency.CAD
    ) {
      try {
        this.spinner(true);

        // Note: Blank paymentMethodId ('') is passed for trials
        await WorkspacePaymentApi.stripeCreateWorkspaceSubscribe({
          priceId: selectedPrice,
          workspaceId: this.activeWorkspaceId,
          paymentMethodId,
          currency,
        });

        if (!paymentMethodId) {
          this.trialActivatingNotification();
        } else {
          this.paymentIsProcessingNotification();
        }

        this.$router.push({
          name: 'projects',
        });
      } catch (err) {
        this.handleError(err);
        console.error(err);
      } finally {
        this.spinner(false);
      }
    },
    async stripeSubUnlimitedUsers(
      paymentMethodId,
      currency = Currency.CAD
    ) {
      try {
        this.spinner(true);
        await WorkspacePaymentApi.stripeWorkspaceSubUnlimitedUsers({
          workspaceId: this.activeWorkspaceId,
          paymentMethodId,
          currency,
        });
        this.paymentIsProcessingNotification();
        setTimeout(() => this.updateActiveWorkspaceData(this.activeWorkspaceId), 10000);
        this.deactivateUnlimitedUsers = true;
      } catch (err) {
        this.handleError(err);
        console.error(err);
      } finally {
        this.spinner(false);
      }
    },
    closePaymentModal() {
      this.showPaymentModal = false;
    },
    getButtonType(type) {
      if (this.isActivePlan(type)) return 'active';
      if (this.isExpiredPlan) return 'resub';
      if (this.eligibleForTrial) return 'trial';

      const wsType = this.getActiveWorkspaceType;
      const getIndex = (workspace) => ORDERED_WORKSPACE_TYPES.findIndex(group => group.includes(workspace));
      const wsTypeIndex = getIndex(wsType);
      const typeIndex = getIndex(type);
      return typeIndex < wsTypeIndex ? 'downgrade' : 'upgrade';
    },
    isActivePlan(type) {
      const wsType = this.getActiveWorkspaceType;
      return wsType.replace(/[-_]/g, '').toLowerCase() === type.replace(/[-_]/g, '').toLowerCase();
    },
    isDowngradePlan(type) {
      return this.getButtonType(type) == 'downgrade';
    },
    openPaymentModal(plan) {
      this.selectedPlanName = plan.name;
      this.selectedPlan = plan.type;
      this.paymentType = plan.type === 'unlimited_users' ? 'PaymentUnlimitedUsers' : 'PaymentsCard';
      this.$nextTick(() => {
        this.showPaymentModal = true;
      });
    },
    planButtonText(type) {
      const btnType = this.getButtonType(type);
      switch (btnType) {
      case 'active':
        return 'Activated';
      case 'trial':
        return 'Try For Free';
      case 'upgrade':
        return 'Upgrade';
      case 'downgrade':
        return 'Downgrade';
      case 'resub':
        return 'Activate';
      default:
        return 'Pay';
      }
    },
  },
};
</script>

<style scoped lang="scss">
.fs-66 {
  font-size: 66px;
  line-height: 1;
}

.v-data-table > .v-data-table__wrapper > table {
  & > thead > tr > th {
    color: var(--v-black-base);
    font-size: 16px;
    font-family: Poppins;
    line-height: 24px;
    font-weight: 700;
  }

  & > tbody > tr > td {
    color: var(--v-black-base);
    font-size: 16px;
    font-family: Poppins;
    line-height: 24px;
    font-weight: 300;

    &:first-child {
      font-weight: 500;
    }
  }
}
</style>